<!-- SettingsModal.vue -->
<template>
  <div class="modal-container">
    <img
      src="images/close.svg"
      class="close"
      @click="resetModal"
      role="button"
      aria-label="Close"
      alt="X to close modal"
      title="Close"
    />
    <div class="modal-content">
      <transition-group :name="titleFade">
        <h2 :key="title">{{ title }}</h2>
      </transition-group>
      <div class="modal-main">
        <knot-carousel
          :slide-class="slideClass"
          :selected-slide="selectedSlide"
          :use-slide-names="true"
          @page-changed="pageChange"
        >
          <template #Settings>
            <div class="modal-options">
              <div class="modal-option-label">
                <div>Save Progress</div>
                <div class="description">
                  Enable local storage to save your game history (<a
                    @click="setModal('Help', 'privacy')"
                    >More Info</a
                  >).
                </div>
              </div>
              <label class="switch">
                <input type="checkbox" v-model="saveProgressToggle" />
                <span class="slider"></span>
              </label>
            </div>
            <div class="modal-options">
              <div class="modal-option-label">
                <div>Show Timer</div>
                <div class="description">Show elapsed time.</div>
              </div>
              <label class="switch">
                <input type="checkbox" v-model="showTimerToggle" />
                <span class="slider"></span>
              </label>
            </div>
            <div class="modal-options">
              <div class="modal-option-label">
                <div>Dark Mode</div>
              </div>
              <label class="switch">
                <input type="checkbox" v-model="darkModeToggle" />
                <span class="slider"></span>
              </label>
            </div>
            <div class="modal-options">
              <div class="modal-option-label">
                <div>Reverse Fill Direction</div>
                <div class="description">
                  {{ reverseFillDescription }}
                </div>
              </div>
              <label class="switch">
                <input type="checkbox" v-model="reverseFillToggle" />
                <span class="slider"></span>
              </label>
            </div>
            <div class="modal-options" v-if="supportsHaptics">
              <div class="modal-option-label">
                <div>Haptic Keyboard</div>
                <div class="description">
                  Enable vibration feedback when entering input.
                </div>
              </div>
              <label class="switch">
                <input type="checkbox" v-model="hapticToggle" />
                <span class="slider"></span>
              </label>
            </div>
            <div class="modal-options">
              <div class="modal-option-label">Share Your Thoughts</div>
              <a :href="this.surveyLink" target="_blank">Feedback</a>
            </div>
            <div class="modal-options">
              <div class="modal-option-label">Report a Bug</div>
              <a :href="bugReport" target="_blank">Email</a>
            </div>
            <div class="version">
              {{ appVersion }}
            </div>
          </template>
          <template #Archive>
            <knot-archive
              :launch-date="launchDate"
              :archive-date="archiveDate"
              :today="today"
              :archiveHistory="archiveHistory"
              @set-puzzle="setPuzzle"
            >
            </knot-archive>
          </template>
        </knot-carousel>
      </div>
    </div>
  </div>
</template>

<script>
import KnotArchive from "./KnotArchive.vue";

export default {
  components: { KnotArchive },
  name: "SettingModal",
  props: {
    resetModal: {
      type: Function,
      required: true,
    },
    archiveDate: {
      type: Date,
    },
    saveProgress: {
      type: Boolean,
      required: true,
    },
    reverseFill: {
      type: Boolean,
      required: true,
    },
    haptic: {
      type: Boolean,
      required: true,
    },
    showTimer: {
      type: Boolean,
      required: true,
    },
    archiveHistory: {
      type: Object,
    },
    selectedSlide: {
      type: String,
    },
    darkMode: {
      type: Boolean,
      required: true,
    },
    defaultTitle: {
      type: String,
      default: "Settings",
    },
    titlesObject: {
      type: Object,
      default: () => ({
        Archive: "Archive",
      }),
    },
  },
  data() {
    return {
      saveProgressToggle: this.saveProgress,
      darkModeToggle: this.darkMode,
      reverseFillToggle: this.reverseFill,
      hapticToggle: this.haptic,
      showTimerToggle: this.showTimer,
      surveyLink: `${process.env.VUE_APP_API_BASE_URL}/survey/`,
      slideClass: { Archive: "at-top no-scroll" },
      today: new Date(),
      launchDate: new Date(Date.parse(process.env.VUE_APP_START_DATE)),
      title:
        this.selectedSlide in this.titlesObject
          ? this.titlesObject[this.selectedSlide]
          : this.defaultTitle,
      titleFade: null,
      appVersion: `v${process.env.VUE_APP_VERSION}`,
    };
  },
  watch: {
    darkModeToggle(value) {
      this.$emit("toggled", "darkMode", value);
    },
    reverseFillToggle(value) {
      this.$emit("toggled", "reverseFill", value);
    },
    saveProgressToggle(value) {
      this.$emit("toggled", "saveProgress", value);
    },
    hapticToggle(value) {
      this.$emit("toggled", "haptic", value);
    },
    showTimerToggle(value) {
      this.$emit("toggled", "showTimer", value);
    },
  },
  computed: {
    reverseFillDescription() {
      if (this.reverseFill) {
        return "Sequences filled right-to-left";
      }
      return "Sequences filled left-to-right";
    },
    supportsHaptics() {
      const isIOS =
        /iPad|iPhone|iPod/.test(navigator.userAgent) ||
        (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

      const isMobile =
        navigator.maxTouchPoints > 0 &&
        /Mobi|Android/i.test(navigator.userAgent);

      return isMobile && !isIOS && "vibrate" in navigator;
    },
    bugReport() {
      const agent = navigator.userAgent;
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      const text = `mailto:KnotilusPuzzle@gmail.com?subject=Knotilus Bug Report&body=
User Agent: ${agent}
Timezone: ${timezone}
Screen Resolution: ${window.screen.width} x ${window.screen.height}
Viewport Size:  ${window.innerWidth} x ${window.innerHeight}
      `;
      return encodeURI(text);
    },
    requestEmail() {
      const text = `mailto:KnotilusPuzzle@gmail.com?subject=Knotilus Playtest Request&body=
I would like to participate in the Knotilus playtest.`;
      return encodeURI(text);
    },
  },
  methods: {
    pageChange(newPageIndex, oldPageIndex, behavior, newPageName) {
      this.title = this.titlesObject[newPageName] || this.defaultTitle;
      if (behavior === "smooth") {
        this.titleFade =
          oldPageIndex < newPageIndex ? "move-right-fade" : "move-left-fade";
      } else {
        this.titleFade = "none";
      }
    },

    setPuzzle(dateValue) {
      this.$emit("set-puzzle", dateValue);
    },
    setModal(key, slide) {
      this.$emit("set-modal", key, slide);
    },
  },
};
</script>

<style scoped>
a {
  cursor: pointer;
}
#calendar {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 2vmin;
  font-size: 1.125em;
}
.calendar-row {
  display: flex;
  justify-content: center;
  flex: 1;
  align-items: center;
}
.calendar-day-header {
  border-bottom: 2px solid var(--border-color);
  font-size: 1.125em;
  font-weight: bold;
  margin: 0 -1.5vmin;
  text-align: center;
}
.calendar-day {
  text-align: center;
  display: flex;
  flex-direction: column;
  grid-gap: var(--key-spacing);
}
.calendar-select {
  aspect-ratio: 1/1;
  border-radius: 10%;
  display: flex;
  align-items: center;
  justify-content: center;
  width: inherit;
}
button.calendar-select {
  border: 1px solid var(--border-color);
  font-size: 3vh;
  color: var(--background-color);
  background-color: var(--primary-color);
  position: relative;
}
button.not-started {
  background-color: var(--background-color);
}
.in-progress {
  opacity: 0.5;
}
.calendar-day span {
  font-weight: bold;
}
.calendar-label {
  display: flex;
  grid-gap: 10px;
}
.calendar-label h2 {
  text-align: left;
  flex: 1;
}
.calendar-select div {
  width: 2vmin;
  height: 2vmin;
  border-radius: 50%;
  background-color: var(--border-color);
  opacity: 50%;
}
.hard-mode::before {
  content: "*";
  position: absolute;
  top: 0;
  font-size: 0.5em;
  font-weight: normal;
}
.change-month {
  aspect-ratio: 1/1;
  height: 2.5vh;
}
.version {
  text-align: right;
}
</style>